import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  MdOpacity, MdPerson, MdCalendarToday, MdOutlinePayment, 
  MdArrowBack, MdAccountBalanceWallet, MdErrorOutline, MdPhoneIphone, MdNumbers 
} from "react-icons/md";
import { baseurl } from "../../baseurl";

const WaterBillDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [billData, setBillData] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [isPaying, setIsPaying] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  
  const guestUserId = localStorage.getItem('guestUserId');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawData = params.get("data");

    if (rawData) {
      try {
        const decodedData = JSON.parse(decodeURIComponent(rawData));
        setBillData(decodedData);
      } catch (err) {
        console.error("Failed to parse water bill data", err);
      }
    }

    if (guestUserId) {
      fetchBalance();
    }
  }, [location, guestUserId]);

  const fetchBalance = () => {
    axios.get(`${baseurl}/getWalletBalance/${guestUserId}`)
      .then(res => setWalletBalance(parseFloat(res.data.balance) || 0))
      .catch(err => console.error("Wallet Error", err));
  };

  const handleProceedToPay = async () => {
    if (!guestUserId) return;
    
    if (!mobileNumber || mobileNumber.length !== 10) {
      alert("Please enter a valid 10-digit mobile number for receipts.");
      return;
    }

    const paymentPayload = {
      guest_id: guestUserId,
      biller_id: billData.biller_id, 
      amount: billData.amount || 0, 
      reference_id: billData.reference_id || "N/A",
      customerParams: billData.customerParams, 
      mobile_number: mobileNumber,
    };

    setIsPaying(true);
    try {
      // Changed endpoint name to reflect Water Bill if your backend supports it, 
      // otherwise keep it as per your API documentation
      const res = await axios.post(`${baseurl}/payWaterBillGuest`, paymentPayload);
      if (res.data.status === "SUCCESS") {
        alert("Water Bill Payment Successful!");
        navigate("/booking");
      } else {
        alert(res.data.message || "Payment Failed");
      }
    } catch (err) {
      console.error("Payment Error:", err);
      alert("An error occurred during payment.");
    } finally {
      setIsPaying(false);
    }
  };

  if (!billData) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-info" role="status"></div>
        <p className="mt-3 text-muted">Loading water bill details...</p>
      </div>
    );
  }

  const amountToPay = parseFloat(billData.amount) || 0;
  const hasInsufficientBalance = walletBalance < amountToPay;
  const isNoDue = amountToPay <= 0;

  return (
    <div className="container py-4 animate-fade-in" style={{ maxWidth: "500px" }}>
      <button onClick={() => navigate(-1)} className="btn btn-link text-decoration-none text-dark p-0 mb-3 d-flex align-items-center">
        <MdArrowBack className="me-1" /> Back
      </button>

      <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: "15px" }}>
        {/* Header Section - Changed to Water Theme Blue */}
        <div className="p-4 text-center text-white" style={{ background: "linear-gradient(135deg, #0077b6 0%, #00b4d8 100%)" }}>
          <div className="bg-white d-inline-block rounded-circle p-3 mb-2 shadow-sm">
            <MdOpacity size={30} style={{ color: "#0077b6" }} />
          </div>
          <h5 className="mb-0 fw-bold">Water Bill Details</h5>
          <p className="x-small opacity-75 mb-0 mt-1">Biller: {billData.biller_id}</p>
        </div>

        {/* Amount & Wallet Info */}
        <div className="bg-light p-4 text-center border-bottom">
          <span className="text-muted small text-uppercase fw-bold d-block mb-1">Total Payable Amount</span>
          <h2 className="fw-bold mb-2" style={{ color: "#0077b6" }}>₹{amountToPay}</h2>
          
          {guestUserId ? (
            <div className={`d-inline-flex align-items-center px-3 py-1 rounded-pill ${hasInsufficientBalance && !isNoDue ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'} `} style={{ fontSize: '12px' }}>
              <MdAccountBalanceWallet className="me-1" /> Wallet Balance: ₹{walletBalance.toFixed(2)}
            </div>
          ) : (
            <div className="badge bg-warning text-dark px-3 py-2"><MdErrorOutline className="me-1" /> Login required</div>
          )}
        </div>

        <div className="p-4">
          {/* Status Message from API */}
          {billData.message && (
             <div className="alert alert-warning py-2 px-3 mb-4 border-0 small" style={{ borderRadius: "10px", backgroundColor: "#fff3cd" }}>
                <strong>Status:</strong> {billData.message}
             </div>
          )}

          <div className="row g-3">
            {/* Customer Name (if available) */}
            {billData.name && (
                <div className="col-12 d-flex align-items-start border-bottom pb-2">
                    <div className="icon-box me-3"><MdPerson size={20} /></div>
                    <div>
                        <label className="text-muted x-small text-uppercase d-block fw-bold">Customer Name</label>
                        <span className="fw-medium text-dark">{billData.name}</span>
                    </div>
                </div>
            )}

            {/* Dynamic Consumer Params Mapping */}
            {billData.customerParams?.map((param, i) => (
              <div key={i} className="col-12 d-flex align-items-start border-bottom pb-2 mt-2">
                <div className="icon-box me-3"><MdNumbers size={20} /></div>
                <div>
                  <label className="text-muted x-small text-uppercase d-block fw-bold">{param.name}</label>
                  <span className="fw-medium text-dark">{param.value}</span>
                </div>
              </div>
            ))}

            {/* Conditional Dates */}
            {billData.billDate && (
                <div className="col-6 d-flex align-items-start mt-3">
                    <div className="icon-box me-3"><MdCalendarToday size={18} /></div>
                    <div>
                        <label className="text-muted x-small text-uppercase d-block fw-bold">Bill Date</label>
                        <span className="fw-medium text-dark">{billData.billDate}</span>
                    </div>
                </div>
            )}
            
            {billData.dueDate && (
                <div className="col-6 d-flex align-items-start mt-3">
                    <div className="icon-box me-3 text-danger"><MdCalendarToday size={18} /></div>
                    <div>
                        <label className="text-muted x-small text-uppercase d-block fw-bold text-danger">Due Date</label>
                        <span className="fw-medium text-danger">{billData.dueDate}</span>
                    </div>
                </div>
            )}
          </div>

          <hr className="my-4" />

          {/* Phone Number Input */}
          <div className="mb-4">
            <label className="fw-bold x-small text-muted text-uppercase mb-2">Notification Mobile Number</label>
            <div className="input-group shadow-sm border rounded-2 overflow-hidden">
              <span className="input-group-text bg-white border-0"><MdPhoneIphone /></span>
              <input 
                type="number" 
                className="form-control border-0 shadow-none py-2" 
                placeholder="Enter 10 digit mobile number"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value.slice(0, 10))}
              />
            </div>
          </div>

          {/* Action Buttons */}
          {!guestUserId ? (
            <button className="btn btn-primary w-100 py-3 fw-bold" onClick={() => navigate('/login')}>Login to Pay</button>
          ) : isNoDue ? (
            <div className="alert alert-success text-center border-0 py-3 mb-0" style={{ borderRadius: '10px' }}>
                <p className="small mb-0 fw-bold">No payment is currently due for this account.</p>
            </div>
          ) : hasInsufficientBalance ? (
            <button className="btn btn-danger w-100 py-3 fw-bold" onClick={() => navigate('/add-money')}>Top-up Wallet (₹{walletBalance})</button>
          ) : (
            <button 
              className="btn w-100 py-3 text-white fw-bold shadow d-flex align-items-center justify-content-center"
              style={{ background: "#0077b6", borderRadius: "10px", fontSize: "1.1rem" }}
              onClick={handleProceedToPay}
              disabled={isPaying}
            >
              {isPaying ? (
                <><span className="spinner-border spinner-border-sm me-2"></span> PROCESSING...</>
              ) : (
                <><MdOutlinePayment className="me-2" /> PAY WATER BILL</>
              )}
            </button>
          )}
        </div>
      </div>

      <style>{`
        .x-small { font-size: 11px; }
        .icon-box { color: #0077b6; background: rgba(0, 119, 182, 0.08); padding: 8px; border-radius: 8px; }
        .bg-success-subtle { background-color: #d1e7dd; color: #0f5132 !important; }
        .bg-danger-subtle { background-color: #f8d7da; color: #842029 !important; }
        .animate-fade-in { animation: fadeIn 0.5s ease-in-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default WaterBillDetail;